import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { TableParameters } from 'app/component-library/table/table-parameters';
import { AuthService } from 'app/core/auth/auth.service';
import { BackendPaginationService } from 'app/core/backend-pagination.service';
import { BackendService } from 'app/core/backend.service';
import { CONFIG } from 'app/core/config';
import { PermissionsService } from 'app/core/permissions.service';
import { HbNotification } from 'app/notifications/notification';
import { NotificationState } from 'app/notifications/notification-state';
import { UserPermissionKeys } from 'app/security/authorization/user-permission-keys';
import { ItemScenarios } from 'app/shared/enums/item-scenarios';
import { RunnableLookupCopyState } from 'app/shared/enums/runnable-lookup-copy-state';
import { ModelStatus } from 'app/viewmodels/model-status';
import { RunnableLookupBriefInfo } from 'app/viewmodels/runnableLookups/runnable-lookup-brief-info';
import { RunnableLookupSaveData } from 'app/viewmodels/runnableLookups/runnable-lookup-save-data';
import { RunnableLookup } from 'app/viewmodels/runnableLookups/runnableLookup';
import { RunnableLookupRow } from 'app/viewmodels/runnableLookups/runnableLookup-row';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NotificationLife } from '../../notifications/notification-life';
import { NotificationService } from '../../notifications/notification.service';
import { ApiResult } from './../../core/messages/api-result';
import { GenerateGuidService } from './../../shared/generate-guid/generate-guid.service';
import { AdminLookupTableService } from './admin-lookup-table-service';

@Injectable({
  providedIn: 'root',
})
export class AdminLookupService {
  public allowDelete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public allowDownload: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public allowEdit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public allowCopy: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public allowEditValues: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public allowSave: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isDeleteVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isAddVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public selectedLookup: BehaviorSubject<RunnableLookupBriefInfo> = new BehaviorSubject<RunnableLookupBriefInfo>(null);
  public savedData: Subject<RunnableLookupSaveData> = new Subject();
  public scenario: ItemScenarios;
  public copyState: RunnableLookupCopyState;

  public isUniqueIdentifier: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  public get hasRows(): boolean {
    return this.adminLookupTableService.hasRows;
  }

  private userPermissionKeys: UserPermissionKeys = new UserPermissionKeys();

  private hasCreatePermission: boolean = false;
  private hasEditPermission: boolean = false;
  private hasDownloadPermission: boolean = false;
  private hasDeletePermission: boolean = false;

  constructor(
    private backendService: BackendService,
    private backendPaginationService: BackendPaginationService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private i18NextPipe: I18NextPipe,
    private permissionsService: PermissionsService,
    private adminLookupTableService: AdminLookupTableService,
    private generateGuidService: GenerateGuidService,
  ) {
    this.subscribeToPermissionChanges();
  }

  private subscribeToPermissionChanges(): void {
    this.permissionsService.userPermissionKeyValuePairs.subscribe(() => {
      this.assignPermissions();
      this.changeButtonsState(this.selectedLookup.value);
    });
  }

  private assignPermissions(): void {
    this.hasCreatePermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.adminLookupCreate);
    this.hasEditPermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.adminLookupEdit);
    this.hasDeletePermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.adminLookupDelete);
    this.hasDownloadPermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.adminLookupDownload);
  }

  public loadRunnableLookup(id: string): void {
    this.scenario = ItemScenarios.Existing;

    this.getRunnableLookup(id).subscribe(lookup => {
      if (lookup) {
        const canEditAttributes = this.canEditAttributes(lookup);
        const canEditValues = this.canEditValues(lookup);
        this.adminLookupTableService.setParams(lookup, canEditAttributes, canEditValues, ItemScenarios.Existing, RunnableLookupCopyState.NoCopy);
        this.adminLookupTableService.loadCollections();
        this.assignSelectedLookup(lookup);
      }
    });
  }

  public checkUniqueIdentifier(identifier: string): void {
    this.hasUniqueIdentifier(identifier).subscribe(result => {
      this.isUniqueIdentifier.next(result);
    });
  }

  public getRunnableLookupRowsCount(tableParameters: TableParameters): Observable<number> {
    return this.backendPaginationService.getCountWithParameters<RunnableLookupRow>(RunnableLookupRow, tableParameters, false);
  }

  public canCopy(lookup: RunnableLookupBriefInfo): boolean {
    return lookup && lookup.lookupId && this.scenario !== ItemScenarios.Copy && this.hasCreatePermission;
  }

  public canDelete(runnableLookup: RunnableLookupBriefInfo): boolean {
    return this.lookupIsExisting(runnableLookup) && runnableLookup.status === ModelStatus.Draft && this.hasDeletePermission;
  }

  public canDownload(lookup: RunnableLookupBriefInfo): boolean {
    return (
      this.lookupIsExisting(lookup) && (lookup.status === ModelStatus.Draft || lookup.status === ModelStatus.Published) && this.hasDownloadPermission
    );
  }

  private canDisplayDelete(lookup: RunnableLookupBriefInfo): boolean {
    return this.lookupIsExisting(lookup) && this.hasDeletePermission;
  }

  private canDisplayAdd(): boolean {
    return this.hasCreatePermission;
  }

  private canEdit(lookup: RunnableLookupBriefInfo): boolean {
    if (this.canEditNew(lookup)) {
      return true;
    }

    return this.canEditExisting(lookup) && lookup.status === ModelStatus.Draft;
  }

  public canEditAttributes(lookup: RunnableLookupBriefInfo): boolean {
    return this.canEdit(lookup);
  }

  private canEditValues(lookup: RunnableLookupBriefInfo): boolean {
    if (this.canEditNew(lookup)) {
      return true;
    }

    return this.canEditExisting(lookup) && (lookup.status === ModelStatus.Draft || lookup.status === ModelStatus.Published);
  }

  private canEditNew(lookup: RunnableLookupBriefInfo): boolean {
    return this.isNewLookup(lookup) && this.hasCreatePermission;
  }

  private canEditExisting(lookup: RunnableLookupBriefInfo): boolean {
    return this.lookupIsExisting(lookup) && this.hasEditPermission;
  }

  private isNewLookup(runnableLookup: RunnableLookupBriefInfo): boolean {
    return !this.lookupIsExisting(runnableLookup);
  }

  private lookupIsExisting(runnableLookup: RunnableLookupBriefInfo): boolean {
    return runnableLookup
      && runnableLookup.lookupId
      && this.scenario === ItemScenarios.Existing;
  }

  public getRunnableLookup(id: string): Observable<RunnableLookupBriefInfo> {
    return this.backendService.getById<RunnableLookupBriefInfo>(RunnableLookupBriefInfo, id);
  }

  public hasUniqueIdentifier(identifier: string): Observable<boolean> {
    const lookupId = this.scenario === ItemScenarios.Existing ? this.selectedLookup.getValue().lookupId : null;

    return this.backendService.postObjectRoute(CONFIG.apiRoutes.runnableLookupHasUniqueIdentifier, { identifier, lookupId }, false);
  }

  public clearSelectedLookup(): void {
    this.assignSelectedLookup(null);
    this.adminLookupTableService.clearTables();
    this.showAttributes(false);
    this.showValues(false);
  }

  private showAttributes(isVisible: boolean): void {
    this.adminLookupTableService.showAttributes.next(isVisible);
  }

  private showValues(isVisible: boolean): void {
    this.adminLookupTableService.showValues.next(isVisible);
  }

  public createNewRunnableLookup(): void {
    this.scenario = ItemScenarios.New;
    const runnableLookup = this.createNew();
    this.assignSelectedLookup(runnableLookup);
    this.adminLookupTableService.clearTables();
    this.adminLookupTableService.setParams(runnableLookup, true, true, this.scenario, RunnableLookupCopyState.NoCopy);
    this.adminLookupTableService.initializeTableConfigs();
    this.showAttributes(true);
    this.showValues(false);
  }

  private createCopiedLookup(source: RunnableLookupBriefInfo): RunnableLookupBriefInfo {
    const copiedLookup = this.createNew();
    copiedLookup.lookupId = this.generateGuidService.newGuid();
    copiedLookup.friendlyTypeNameSingular = source.friendlyTypeNameSingular;

    return copiedLookup;
  }

  private createNew(): RunnableLookupBriefInfo {
    const lookup = new RunnableLookupBriefInfo();
    lookup.createdBy = this.authService.loggedInUser.value;
    lookup.createdClientSide = true;
    lookup.lookupId = this.generateGuidService.newGuid();
    lookup.status = ModelStatus.Draft;
    lookup.creationDate = moment.utc().toISOString();

    return lookup;
  }

  public createCopy(copyState: RunnableLookupCopyState = RunnableLookupCopyState.CopyWithValues): void {
    const source = this.selectedLookup.getValue();

    if (!source || copyState === RunnableLookupCopyState.NoCopy) {
      return;
    }

    this.scenario = ItemScenarios.Copy;
    this.copyState = copyState;

    const copiedLookup = this.createCopiedLookup(source);

    this.adminLookupTableService.setParams(source, false, false, this.scenario, copyState);
    this.adminLookupTableService.copy(copyState);
    this.assignSelectedLookup(copiedLookup);
  }

  public delete(id: string): Observable<RunnableLookup> {
    return this.backendService.delete<RunnableLookup>(RunnableLookup, id);
  }

  public createRunnableLookup(saveData: RunnableLookupSaveData): Observable<RunnableLookupSaveData> {
    return this.backendService.create<RunnableLookupSaveData>(RunnableLookupSaveData, saveData);
  }

  public updateRunnableLookup(saveData: RunnableLookupSaveData): Observable<RunnableLookupSaveData> {
    return this.backendService.update<RunnableLookupSaveData>(RunnableLookupSaveData, saveData.briefInfo.lookupId, saveData);
  }

  public showFailedNotification(description: string): void {
    this.showNotification(description, NotificationState.Failed);
  }

  public showCompletedNotification(description: string): void {
    this.showNotification(description, NotificationState.Complete);
  }

  private assignSelectedLookup(runnableLookup: RunnableLookupBriefInfo): void {
    this.changeButtonsState(runnableLookup);
    this.selectedLookup.next(runnableLookup);
  }

  private changeButtonsState(runnableLookup: RunnableLookupBriefInfo): void {
    this.allowDelete.next(this.canDelete(runnableLookup));
    this.allowDownload.next(this.canDownload(runnableLookup));
    this.allowEdit.next(this.canEdit(runnableLookup));
    this.allowCopy.next(this.canCopy(runnableLookup));
    this.isDeleteVisible.next(this.canDisplayDelete(runnableLookup));
    this.isAddVisible.next(this.canDisplayAdd());
    this.allowEditValues.next(this.canEditValues(runnableLookup));
    this.allowSave.next(this.allowEdit.value || this.allowEditValues.value);
  }

  private showNotification(description: string, state: NotificationState): void {
    const notification = new HbNotification();
    notification.description = description;
    notification.state = state;
    notification.life = NotificationLife.Long;
    this.notificationService.displayToast(notification);
  }

  public onSaveClicked(processSaveResultCallback: (result: RunnableLookupSaveData) => void): void {
    const lookup = this.selectedLookup.getValue();

    const saveData: RunnableLookupSaveData = this.adminLookupTableService.createSaveData(lookup);
    const method: Observable<RunnableLookupSaveData> = this.scenario !== ItemScenarios.Existing
      ? this.createRunnableLookup(saveData)
      : this.updateRunnableLookup(saveData);

    method.subscribe(
      result => {
        if (result) {
          this.loadRunnableLookup(result.briefInfo.lookupId);
          this.showCompletedSaveNotification();
          processSaveResultCallback(result);
        } else {
          this.showFailedSaveNotification();
        }
      },
      (errorResult: ApiResult) => {
        if (errorResult && errorResult.messages && errorResult.messages.length > 0) {
          errorResult.messages.forEach(mes => this.showFailedSaveNotification(mes.text));
        } else {
          this.showFailedSaveNotification();
        }
      },
    );
  }

  private showCompletedSaveNotification(): void {
    this.showCompletedNotification(this.i18NextPipe.transform('AdminLookup.AdminLookupSaved'));
  }

  private showFailedSaveNotification(errorMessage: string = this.i18NextPipe.transform('AdminLookup.AdminLookupSaveError')): void {
    this.showFailedNotification(errorMessage);
  }
}
